/**
 * Chart commands for the palette (SPEC §17.5, SPEC-ADDENDA A1.4 `history.reload`). They act on the
 * most recently focused chart; results appear as that chart's notice.
 */
import type { CommandRegistry } from "@helm/core";
import { reloadFocusedChartHistory } from "./historyReload";
import { chartCount, renderHost } from "./renderHost";
import { chartSettingsTargets, openChartSettings } from "./chartSettings";
import { chartTemplatesTargets, openChartTemplates } from "./chartTemplates";

export function registerChartCommands(r: CommandRegistry): void {
  r.register({
    id: "chart.settings",
    title: "Chart: Chart settings…",
    group: "chart",
    keywords: [
      "style",
      "candles",
      "heikin",
      "line",
      "area",
      "log",
      "percent",
      "scale",
      "invert",
      "colors",
      "appearance",
    ],
    when: () => chartSettingsTargets() > 0,
    run: () => {
      openChartSettings();
    },
  });
  r.register({
    id: "chart.template.apply",
    title: "Chart: Apply chart template…",
    group: "chart",
    keywords: ["template", "look", "layout", "style", "indicators", "apply"],
    when: () => chartTemplatesTargets() > 0,
    run: () => {
      openChartTemplates(null, "apply");
    },
  });
  r.register({
    id: "chart.template.save",
    title: "Chart: Save chart template…",
    group: "chart",
    keywords: ["template", "save", "look", "style", "indicators"],
    when: () => chartTemplatesTargets() > 0,
    run: () => {
      openChartTemplates(null, "save");
    },
  });
  r.register({
    id: "history.reload",
    title: "Chart: Reload history",
    group: "chart",
    keywords: ["history", "refetch", "broken", "data", "bars"],
    when: () => chartCount() > 0,
    run: () => {
      reloadFocusedChartHistory("spec");
    },
  });
  r.register({
    id: "history.reload.visible",
    title: "Chart: Reload history (visible range)",
    group: "chart",
    keywords: ["history", "refetch", "broken", "visible", "range", "suspect"],
    when: () => chartCount() > 0,
    run: () => {
      reloadFocusedChartHistory("visible");
    },
  });
  r.register({
    id: "history.reload.symbol",
    title: "Chart: Reload history (all timeframes)",
    group: "chart",
    keywords: ["history", "refetch", "symbol"],
    when: () => chartCount() > 0,
    run: () => {
      reloadFocusedChartHistory("symbol");
    },
  });
  r.register({
    id: "chart.resetView",
    title: "Chart: Reset chart view",
    group: "chart",
    keywords: ["reset", "view", "zoom", "live", "fit", "alt+r"],
    when: () => chartCount() > 0,
    run: () => {
      renderHost().resetView();
    },
  });
  r.register({
    id: "history.clear",
    title: "Chart: Clear all cached history",
    group: "chart",
    keywords: ["history", "cache", "storage", "indexeddb", "clear"],
    run: () => {
      renderHost().clearHistoryCache();
    },
  });
}
